export type FactorStance = 'above' | 'below' | 'level';

export interface FactorRangeInput {
  /** The company's score for the factor. */
  readonly value: number | null | undefined;
  /** The industry average for the factor. */
  readonly industry: number | null | undefined;
  /** Lowest score seen in the industry. */
  readonly min: number | null | undefined;
  /** Highest score seen in the industry. */
  readonly max: number | null | undefined;
}

export interface FactorRangeGeometry {
  /** Left end of the drawn scale. Wider than `min` when a score falls below it. */
  readonly start: number;
  /** Right end of the drawn scale. Wider than `max` when a score rises above it. */
  readonly end: number;
  /** The industry's lowest score, which the coloured bar starts at. */
  readonly min: number;
  /** The industry's highest score, which the coloured bar ends at. */
  readonly max: number;
  /** Where the industry's range starts and ends along the scale, 0 to 100. */
  readonly rangeStartPct: number;
  readonly rangeEndPct: number;
  /** Where the company score is against the industry's range: past either end, or within it. */
  readonly place: 'below' | 'within' | 'above';
  /** Where the company dot sits, 0 to 100 along the scale. */
  readonly valuePct: number;
  /** Where the industry tick sits, 0 to 100 along the scale. */
  readonly industryPct: number;
  /** The company score against the industry average, judged on the numbers shown. */
  readonly stance: FactorStance;
  /** Shown company score minus shown industry average. */
  readonly gap: number;
}

const isScore = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n);

/** A score as it is shown: one decimal at most. */
export const roundScore = (n: number): number => Math.round(n * 10) / 10;

/**
 * Positions one factor on its industry scale, or returns null when any of the four numbers is missing: a graph
 * drawn from three of them would say something the data does not.
 *
 * The scale runs from the industry's min to its max. When the company score or the industry average falls outside
 * that, the scale widens to hold it, and the industry's own range is still marked on it, so a company below every peer
 * is drawn past the end of the range and not as if it were the lowest of them. Clamping instead would draw the dot on
 * the edge. Bounds given the wrong way round are swapped. When the scale has no width, every mark sits in the
 * middle.
 *
 * The stance compares the rounded numbers, so a label reading 58 against an average reading 58 is never coloured
 * as above or below.
 */
export function factorRangeGeometry({
  value,
  industry,
  min,
  max,
}: FactorRangeInput): FactorRangeGeometry | null {
  if (!isScore(value) || !isScore(industry) || !isScore(min) || !isScore(max)) {
    return null;
  }

  const start = Math.min(min, max, value, industry);
  const end = Math.max(min, max, value, industry);
  const span = end - start;
  const along = (n: number) => (span === 0 ? 50 : ((n - start) / span) * 100);

  const gap = roundScore(roundScore(value) - roundScore(industry));
  let stance: FactorStance = 'level';
  if (gap > 0) {
    stance = 'above';
  } else if (gap < 0) {
    stance = 'below';
  }

  const low = Math.min(min, max);
  const high = Math.max(min, max);
  let place: FactorRangeGeometry['place'] = 'within';
  if (value < low) {
    place = 'below';
  } else if (value > high) {
    place = 'above';
  }

  return {
    start,
    end,
    min: low,
    max: high,
    rangeStartPct: along(low),
    rangeEndPct: along(high),
    place,
    valuePct: along(value),
    industryPct: along(industry),
    stance,
    gap,
  };
}

/** The gap in words, signed, for the detailed size: "+3 vs industry", "At industry average". */
export function gapText({ gap }: Pick<FactorRangeGeometry, 'gap'>): string {
  if (gap === 0) {
    return 'At industry average';
  }
  return `${gap > 0 ? '+' : '-'}${Math.abs(gap)} vs industry`;
}

/**
 * The accessible name of the graph: the numbers the picture shows and whether the company is ahead of or behind
 * the average, so colour is never the only way to know.
 */
export function describeFactorRange(
  input: FactorRangeInput,
  label?: string
): string {
  const prefix = label ? `${label}: ` : '';
  const geometry = factorRangeGeometry(input);
  if (!isScore(input.value)) {
    return `${prefix}not scored`;
  }
  if (!geometry || !isScore(input.industry)) {
    return `${prefix}${roundScore(input.value)}, no industry comparison`;
  }
  const { min, max, place, stance, gap } = geometry;
  let versus = 'level with the industry average';
  if (stance === 'above') {
    versus = `${Math.abs(gap)} above the industry average`;
  } else if (stance === 'below') {
    versus = `${Math.abs(gap)} below the industry average`;
  }
  let range = `industry range ${roundScore(min)} to ${roundScore(max)}`;
  if (place === 'below') {
    range = `below the industry range of ${roundScore(min)} to ${roundScore(
      max
    )}`;
  } else if (place === 'above') {
    range = `above the industry range of ${roundScore(min)} to ${roundScore(
      max
    )}`;
  }
  return `${prefix}${roundScore(input.value)}, ${versus} of ${roundScore(
    input.industry
  )}, ${range}`;
}
